import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ChatAttachment,
  ChatConversationDetail,
  ChatDiagnostics,
  ChatLeaveResult,
  ChatManualStatus,
  ChatMessage,
  ChatMessagePage,
  ChatPerson,
  ChatPresenceEvent,
  ChatPresenceResult,
  ChatReactionUpdate,
  ChatReadResult,
  ChatSidebarData,
  ChatSyncResult,
} from '../models/chat.models';

/** The `{ status, status_code, message, data }` envelope every v1 endpoint returns. */
interface ChatEnvelope<T> {
  status?: boolean;
  status_code?: number;
  message?: string;
  data?: T;
}

/**
 * HTTP facade over the chat backend — one method per endpoint in `docs/backend/chat/internal-chat.md`.
 *
 * Every method unwraps `data` and hands back the typed payload; the envelope, auth header and
 * error toasting are {@link ApiService}'s job. Nothing here holds state — {@link ChatStore} owns
 * all of it.
 *
 * All 24 endpoints are live (`docs/backend/chat/internal-chat-backend.md`). {@link ChatBackendService}
 * is still what callers inject: it adds the session-wide "no chat API answering here" flag that
 * puts the "Please refresh your browser" state on screen, and never substitutes data of its own.
 */
@Injectable({ providedIn: 'root' })
export class ChatApiService {
  constructor(
    private readonly api: ApiService,
    private readonly http: HttpClient
  ) {}

  // ---- Sidebar & directory ---------------------------------------------------

  /** Everything the sidebar renders, in one request (§6.1). */
  getSidebar(): Observable<ChatSidebarData> {
    return this.api.get('/chat/conversations').pipe(map((res: ChatEnvelope<ChatSidebarData>) => res?.data as ChatSidebarData));
  }

  /**
   * What has changed since `since` — two indexed queries instead of rebuilding the sidebar.
   *
   * This is what the safety-net poll asks, not `getSidebar()`: the answer is normally an empty
   * `conversations` array, which is the whole point of running it twice a minute per open tab.
   * Omitting `since` returns every conversation, which is what a cold cursor wants.
   */
  sync(since?: string): Observable<ChatSyncResult> {
    const query = since ? `?since=${encodeURIComponent(since)}` : '';
    return this.api.get(`/chat/sync${query}`).pipe(map((res: ChatEnvelope<ChatSyncResult>) => res?.data as ChatSyncResult));
  }

  /**
   * Which API process answered and what sockets it holds.
   *
   * Not used by any screen — it exists so {@link ChatStore.runDiagnostics} can settle whether a
   * realtime fault is a multi-process fan-out problem or something per-user, without anyone
   * having to read a load balancer config. See `internal-chat-frontend-handover.md` §4.
   */
  getDiagnostics(): Observable<ChatDiagnostics> {
    return this.api.get('/chat/diagnostics').pipe(map((res: ChatEnvelope<ChatDiagnostics>) => (res?.data ?? {}) as ChatDiagnostics));
  }

  /** Office colleagues for the group member picker (§6.1). */
  getUsers(search = ''): Observable<ChatPerson[]> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.api.get(`/chat/users${query}`).pipe(map((res: ChatEnvelope<ChatPerson[]>) => res?.data ?? []));
  }

  /** People + groups matching a sidebar search term (§6.5). */
  search(term: string): Observable<{ people: ChatPerson[]; groups: any[] }> {
    return this.api
      .get(`/chat/search?q=${encodeURIComponent(term)}`)
      .pipe(map((res: ChatEnvelope<{ people: ChatPerson[]; groups: any[] }>) => res?.data ?? { people: [], groups: [] }));
  }

  /** Master-admin-only (`docs/backend/chat/internal-chat-hide-user.md` §2) — 403s for anyone else. */
  setUserHidden(userId: string, hidden: boolean): Observable<ChatPerson> {
    return this.api.patch(`/chat/users/${userId}`, { is_hidden: hidden }).pipe(map((res: ChatEnvelope<ChatPerson>) => res?.data as ChatPerson));
  }

  // ---- Conversations ---------------------------------------------------------

  /** Get-or-create the DM with `userId`; idempotent server-side (§6.2). */
  openDirect(userId: string): Observable<{ conversation_id: string; created: boolean }> {
    return this.api
      .post('/chat/conversations/direct', { user_id: userId })
      .pipe(map((res: ChatEnvelope<{ conversation_id: string; created: boolean }>) => res?.data as any));
  }

  getConversation(conversationId: string): Observable<ChatConversationDetail> {
    return this.api
      .get(`/chat/conversations/${conversationId}`)
      .pipe(map((res: ChatEnvelope<ChatConversationDetail>) => res?.data as ChatConversationDetail));
  }

  // ---- Messages --------------------------------------------------------------

  /**
   * One page of history, oldest-first. `before` pages backwards through the scrollback;
   * `after` fills the gap after a socket reconnect.
   *
   * The two cursors are mutually exclusive server-side, so `after` wins if a caller ever passes
   * both — a gap fill asks for everything newer, which is the stricter of the two intents.
   */
  getMessages(conversationId: string, options: { limit?: number; before?: string; after?: string } = {}): Observable<ChatMessagePage> {
    const params = new URLSearchParams();
    params.set('limit', String(options.limit ?? 50));
    if (options.after) {
      params.set('after', options.after);
    } else if (options.before) {
      params.set('before', options.before);
    }
    return this.api
      .get(`/chat/conversations/${conversationId}/messages?${params.toString()}`)
      .pipe(map((res: ChatEnvelope<ChatMessagePage>) => res?.data as ChatMessagePage));
  }

  /**
   * Send. `clientMsgId` is the idempotency key — a retry with the same id returns the original
   * message instead of posting twice, and it is what lets the optimistic bubble reconcile.
   */
  sendMessage(conversationId: string, clientMsgId: string, body: string, attachmentIds: string[] = []): Observable<ChatMessage> {
    return this.api
      .post(`/chat/conversations/${conversationId}/messages`, {
        client_msg_id: clientMsgId,
        body,
        attachment_ids: attachmentIds,
      })
      .pipe(map((res: ChatEnvelope<ChatMessage>) => res?.data as ChatMessage));
  }

  /** Mark read up to a message; returns the caller's new global badge and this thread's count. */
  markRead(conversationId: string, lastReadMessageId: string): Observable<ChatReadResult> {
    return this.api
      .post(`/chat/conversations/${conversationId}/read`, { last_read_message_id: lastReadMessageId })
      .pipe(map((res: ChatEnvelope<ChatReadResult>) => res?.data ?? { total_unread: 0 }));
  }

  editMessage(messageId: string, body: string): Observable<ChatMessage> {
    return this.api.patch(`/chat/messages/${messageId}`, { body }).pipe(map((res: ChatEnvelope<ChatMessage>) => res?.data as ChatMessage));
  }

  deleteMessage(messageId: string): Observable<unknown> {
    return this.api.delete(`/chat/messages/${messageId}`);
  }

  /** Both reaction endpoints answer with the recomputed array, not the whole message. */
  addReaction(messageId: string, emoji: string): Observable<ChatReactionUpdate> {
    return this.api
      .post(`/chat/messages/${messageId}/reactions`, { emoji })
      .pipe(map((res: ChatEnvelope<ChatReactionUpdate>) => this.reactionUpdate(messageId, res?.data)));
  }

  /** The emoji is a path segment, so it must be percent-encoded. */
  removeReaction(messageId: string, emoji: string): Observable<ChatReactionUpdate> {
    return this.api
      .delete(`/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`)
      .pipe(map((res: ChatEnvelope<ChatReactionUpdate>) => this.reactionUpdate(messageId, res?.data)));
  }

  private reactionUpdate(messageId: string, data: ChatReactionUpdate | undefined): ChatReactionUpdate {
    return { message_id: data?.message_id || messageId, reactions: data?.reactions ?? [] };
  }

  // ---- Groups ----------------------------------------------------------------

  createGroup(name: string, memberIds: string[]): Observable<ChatConversationDetail> {
    return this.api
      .post('/chat/groups', { name, member_ids: memberIds })
      .pipe(map((res: ChatEnvelope<ChatConversationDetail>) => res?.data as ChatConversationDetail));
  }

  renameGroup(conversationId: string, name: string): Observable<ChatConversationDetail> {
    return this.api
      .patch(`/chat/groups/${conversationId}`, { name })
      .pipe(map((res: ChatEnvelope<ChatConversationDetail>) => res?.data as ChatConversationDetail));
  }

  addMembers(conversationId: string, userIds: string[]): Observable<ChatConversationDetail> {
    return this.api
      .post(`/chat/groups/${conversationId}/members`, { user_ids: userIds })
      .pipe(map((res: ChatEnvelope<ChatConversationDetail>) => res?.data as ChatConversationDetail));
  }

  removeMember(conversationId: string, userId: string): Observable<ChatConversationDetail> {
    return this.api
      .delete(`/chat/groups/${conversationId}/members/${userId}`)
      .pipe(map((res: ChatEnvelope<ChatConversationDetail>) => res?.data as ChatConversationDetail));
  }

  /** `deleted` comes back true when the caller was the last member and the group went with them. */
  leaveGroup(conversationId: string): Observable<ChatLeaveResult> {
    return this.api.post(`/chat/groups/${conversationId}/leave`, {}).pipe(map((res: ChatEnvelope<ChatLeaveResult>) => res?.data ?? {}));
  }

  /** Duplicate a group's membership under a new name. Messages are not copied (§6.4). */
  copyGroup(conversationId: string, name?: string): Observable<ChatConversationDetail> {
    return this.api
      .post(`/chat/groups/${conversationId}/copy`, name ? { name } : {})
      .pipe(map((res: ChatEnvelope<ChatConversationDetail>) => res?.data as ChatConversationDetail));
  }

  deleteGroup(conversationId: string): Observable<unknown> {
    return this.api.delete(`/chat/groups/${conversationId}`);
  }

  // ---- Presence --------------------------------------------------------------

  getPresence(): Observable<ChatPresenceEvent[]> {
    return this.api.get('/chat/presence').pipe(map((res: ChatEnvelope<ChatPresenceEvent[]>) => res?.data ?? []));
  }

  /** Returns the effective `status` plus the `manual_status` that was actually stored. */
  setPresence(status: ChatManualStatus): Observable<ChatPresenceResult> {
    return this.api.post('/chat/presence', { status }).pipe(map((res: ChatEnvelope<ChatPresenceResult>) => res?.data ?? {}));
  }

  getUnreadCount(): Observable<number> {
    return this.api.get('/chat/unread-count').pipe(map((res: ChatEnvelope<{ total_unread: number }>) => res?.data?.total_unread ?? 0));
  }

  // ---- Attachments -----------------------------------------------------------

  /**
   * Upload one file, then attach the returned id to a message.
   *
   * Uses `HttpClient` directly rather than {@link ApiService}: the shared helpers force a JSON
   * content type, and multipart needs the browser to set its own boundary. The auth interceptor
   * still attaches the bearer.
   */
  uploadAttachment(conversationId: string, file: File, durationMs?: number): Observable<ChatAttachment> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('conversation_id', conversationId);
    if (durationMs != null) {
      form.append('duration_ms', String(Math.round(durationMs)));
    }

    const baseUrl = `${environment.nodeApiUrl}/${environment.api_prefix}`;
    return this.http.post<ChatEnvelope<ChatAttachment>>(`${baseUrl}/chat/attachments`, form).pipe(map(res => res?.data as ChatAttachment));
  }

  /**
   * Where a download link should point.
   *
   * `download_url` is a signed S3 URL carrying `response-content-disposition=attachment`, so it
   * needs no `Authorization` header and an `<a href download>` actually saves the file. That is
   * the whole point of it — the bearer-protected API route (`download_endpoint`) cannot be used
   * from an anchor at all, and is only a fallback here for a payload that predates the change.
   */
  downloadUrl(attachment: Pick<ChatAttachment, 'attachment_id' | 'download_url' | 'download_endpoint'>): string {
    if (attachment?.download_url) {
      return attachment.download_url;
    }
    const endpoint = attachment?.download_endpoint;
    if (endpoint) {
      return /^https?:\/\//i.test(endpoint) ? endpoint : `${environment.nodeApiUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
    }
    return `${environment.nodeApiUrl}/${environment.api_prefix}/chat/attachments/${attachment?.attachment_id}/download`;
  }
}
